import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { commentsDb } from '../../lib/db/index.js';
import { useAuth } from '../../lib/firebase/auth-context.js';
import { useUser } from '../../lib/firebase/use-user.js';
import type { Comment, CommentTargetType, CommentType } from '../../types/comment.js';
import { COMMENT_TYPES } from '../../types/comment.js';
import type { Visibility } from '../../types/visibility.js';
import { Spinner } from '../shared/Spinner.js';
import { EmptyState } from '../shared/EmptyState.js';
import { Avatar } from '../shell/Avatar.js';
import { CommentTypeBadge } from './CommentTypeBadge.js';
import { Markdown } from '../shared/Markdown.js';
import { commentInputSchema } from '../../lib/schemas/comment.js';
import { toAppError } from '../../lib/utils/error.js';
import { timeAgo } from '../../lib/utils/time.js';

interface Props {
  targetType: CommentTargetType;
  targetId: string;
  /** 親docの visibility（クエリのフィルタを Rules 互換にするため必須） */
  parentVisibility: Visibility;
}

export function CommentList({ targetType, targetId, parentVisibility }: Props) {
  const { fbUser } = useAuth();
  const uid = fbUser?.uid;
  const mode: 'shared' | 'mine' = parentVisibility === 'shared' ? 'shared' : 'mine';
  const q = useQuery({
    queryKey: ['comments', targetType, targetId, mode, uid ?? null],
    queryFn: () =>
      commentsDb.findByTarget(targetType, targetId, { count: 50, mode, uid }),
    enabled: mode === 'shared' || !!uid,
  });

  if (q.isPending) return <div className="section-loading"><Spinner /></div>;
  if (!q.data || q.data.length === 0) {
    return <EmptyState title="まだコメントはありません" />;
  }

  return (
    <ul className="comment-list">
      {q.data.map((c) => (
        <CommentItem
          key={c.id}
          comment={c}
          isOwner={!!uid && uid === c.createdBy}
          targetType={targetType}
          targetId={targetId}
          mode={mode}
        />
      ))}
    </ul>
  );
}

interface CommentItemProps {
  comment: Comment;
  isOwner: boolean;
  targetType: CommentTargetType;
  targetId: string;
  mode: 'shared' | 'mine';
}

function CommentItem({ comment: c, isOwner, targetType, targetId, mode }: CommentItemProps) {
  const qc = useQueryClient();
  const { fbUser } = useAuth();
  const uid = fbUser?.uid;
  const author = useUser(c.createdBy);
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [body, setBody] = useState(c.body);
  const [type, setType] = useState<CommentType>(c.type);
  const [error, setError] = useState<string | null>(null);

  const queryKey = ['comments', targetType, targetId, mode, uid ?? null];

  const m = useMutation({
    mutationFn: async () => {
      // 既存スキーマを部分的に流用（targetType/targetId は不変、ここでは body/type のみ検証）
      const parsed = commentInputSchema.pick({ body: true, type: true }).parse({ body, type });
      await commentsDb.update(c.id, { body: parsed.body, type: parsed.type as CommentType });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey });
      setEditing(false);
      setError(null);
    },
    onError: (err) => setError(toAppError(err).userMessage),
  });

  // 削除（本人のみ。Rules は本人 or 管理者を許可するが、今回はモデレーション未対応）
  const del = useMutation({
    mutationFn: () => commentsDb.remove(c.id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey });
    },
    onError: (err) => setError(toAppError(err).userMessage),
  });

  const cancel = () => {
    setBody(c.body);
    setType(c.type);
    setError(null);
    setEditing(false);
  };

  const authorName = author.data?.name ?? `@${c.createdBy.slice(0, 6)}`;

  return (
    <li className="comment-item">
      {author.data ? (
        <Avatar user={author.data} size="sm" />
      ) : (
        <span className="avatar sm comment-avatar-fallback" aria-hidden>?</span>
      )}
      <div className="comment-main">
        <div className="comment-head">
          <span className="comment-author">{authorName}</span>
          <span className="dot-sep">·</span>
          <time className="comment-time mono">{timeAgo(c.createdAt)}</time>
          {isOwner && !editing && !confirmingDelete && (
            <CommentMenu
              onEdit={() => {
                setError(null);
                setEditing(true);
              }}
              onDelete={() => {
                setError(null);
                setConfirmingDelete(true);
              }}
            />
          )}
        </div>

        {editing ? (
          <div className="comment-body">
            <select
              value={type}
              onChange={(e) => setType(e.target.value as CommentType)}
              style={{ marginBottom: 6 }}
            >
              {COMMENT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              maxLength={2000}
              style={{ width: '100%' }}
            />
            {error && <div className="auth-error">{error}</div>}
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <button
                type="button"
                className="btn btn-primary xs"
                onClick={() => {
                  setError(null);
                  m.mutate();
                }}
                disabled={m.isPending}
              >
                {m.isPending ? <Spinner /> : '保存'}
              </button>
              <button type="button" className="btn xs" onClick={cancel} disabled={m.isPending}>
                キャンセル
              </button>
            </div>
          </div>
        ) : (
          <div className="comment-body">
            <CommentTypeBadge value={c.type} />
            <Markdown>{c.body}</Markdown>
            {confirmingDelete && (
              <div className="comment-confirm">
                <span>削除しますか？</span>
                <button
                  type="button"
                  className="btn btn-destructive xs"
                  onClick={() => del.mutate()}
                  disabled={del.isPending}
                >
                  {del.isPending ? <Spinner /> : 'はい'}
                </button>
                <button
                  type="button"
                  className="btn xs"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={del.isPending}
                >
                  いいえ
                </button>
              </div>
            )}
            {error && !editing && <div className="auth-error">{error}</div>}
          </div>
        )}
      </div>
    </li>
  );
}

/** 投稿本人向けの操作メニュー（編集・削除）。外側クリックで閉じる。 */
function CommentMenu({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="comment-menu" ref={ref}>
      <button
        type="button"
        className="comment-menu-trigger"
        aria-label="コメントの操作"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        ⋯
      </button>
      {open && (
        <div className="comment-menu-pop" role="menu">
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onEdit();
            }}
          >
            編集
          </button>
          <button
            type="button"
            role="menuitem"
            className="danger"
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
          >
            削除
          </button>
        </div>
      )}
    </div>
  );
}
